import { ReactNode, useCallback, useMemo, useRef } from 'react'

import { EditorContent } from '@tiptap/react'
import { FileDrop } from 'react-file-drop'

import { isExactlyOneEmoji } from '@bettermode/common/utils'

import { ChatAttachment } from '../Messaging/types.js'
import { GiphyPickerModal } from '../Picker/GiphyPickerModal.js'
import { ImagePickerModal } from '../Picker/ImagePickerModal.js'

import { LinkFormDialog } from './components/LinkFormDialog.js'
import { LinkFormattingBubbleMenu } from './extensions/bubble-menu/link.js'
import { TextFormattingBubbleMenu } from './extensions/bubble-menu/text.js'
import { useComposer } from './hooks/useComposer.js'
import { isImage } from './utils/image.js'

export interface ComposerProps {
  children?: ReactNode
  /**
   * Specifies the minimum size of the editor based on number of lines
   * Default: 1
   */
  minLines?: number
  onAttachmentAdded?: (files: File[], type: ChatAttachment['type']) => void
}

export const ChatComposer = ({
  children,
  minLines = 1,
  onAttachmentAdded,
}: ComposerProps) => {
  const { state, toggleDialog, editor } = useComposer()

  const onEditorClick = useCallback(() => {
    if (editor && editor.isEmpty && !editor.isFocused) {
      editor.commands.focus()
    }
  }, [editor])

  const menuContainerRef = useRef(null)

  const onDrop = useCallback(
    (fileList: FileList) => {
      const filesArray = Array.from(fileList ?? [])
      const images = filesArray.filter(isImage)
      const files = filesArray.filter(file => !isImage(file))
      onAttachmentAdded(images, 'image')
      onAttachmentAdded(files, 'file')
    },
    [onAttachmentAdded],
  )
  // Storing in a variable to have a stable reference for the useMemo
  const editorText = editor?.getText()
  const isSingleEmoji = useMemo(
    () => isExactlyOneEmoji(editorText),
    [editorText],
  )

  if (!editor) {
    return null
  }

  return (
    <>
      <div
        ref={menuContainerRef}
        id="composer"
        className="h-full flex-col flex flex-1 min-h-[60px] max-h-[15rem]"
      >
        <TextFormattingBubbleMenu editor={editor} />
        <LinkFormattingBubbleMenu editor={editor} />
        <FileDrop
          className="flex flex-col flex-grow overflow-y-scroll"
          targetClassName="flex flex-col flex-grow"
          onDrop={onDrop}
        >
          <article
            className="flex flex-col flex-grow editor-content"
            dir="auto"
          >
            <EditorContent
              editor={editor}
              onClick={onEditorClick}
              style={{
                minHeight: `${minLines}em`,
                fontSize: isSingleEmoji ? '2rem' : undefined,
              }}
              className="text-md text-content relative flex-1 mb-4 flex flex-col pt-1 prose-mb-sm"
            />
          </article>
        </FileDrop>
        {children}
      </div>
      {/* These dialogs should have lived within the provider but due to a issue with the modal portals we need to keep them close to the editor  */}
      <ImagePickerModal
        open={state.image}
        multiple
        close={() => toggleDialog('image', false)}
        upload={files => {
          onAttachmentAdded(files, 'image')
          toggleDialog('image', false)
        }}
        tabs={['upload']}
      />
      <GiphyPickerModal
        open={state.giphy}
        close={() => toggleDialog('giphy', false)}
        onUpload={file => {
          toggleDialog('giphy', false)
          onAttachmentAdded([file], 'giphy')
        }}
      />
      <LinkFormDialog
        open={state.link}
        onClose={() => toggleDialog('link', false)}
        onSubmit={data => {
          const { link, text } = data
          if (link) {
            editor
              .chain()
              .focus()
              .insertLink({ href: link, text })
              .insertContent(' ')
              .run()
          }
          toggleDialog('link', false)
        }}
      />
    </>
  )
}
